import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BillingCheckoutInput, BillingDetail, CheckoutUrlResponse, CreditLedgerEntry } from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

// §24.1 - billing is money/plan truth, always read live: staleTime 0, no cache.
export function useBillingDetail(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'billing'],
    queryFn: () => api.get<BillingDetail>(workspacePath(workspaceId!, '/billing')),
    enabled: !!workspaceId,
    staleTime: 0,
  });
}

export function useCreditLedger(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'billing', 'ledger'],
    queryFn: () => api.get<{ items: CreditLedgerEntry[]; nextCursor: string | null }>(workspacePath(workspaceId!, '/billing/ledger')),
    enabled: !!workspaceId,
    staleTime: 0,
  });
}

export function useCheckout(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingCheckoutInput) => api.post<CheckoutUrlResponse>(workspacePath(workspaceId!, '/billing/checkout'), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'billing'] }),
  });
}

export function usePortalSession(workspaceId: string | null) {
  return useMutation({
    mutationFn: () => api.post<CheckoutUrlResponse>(workspacePath(workspaceId!, '/billing/portal')),
  });
}

export function useTopupCheckout(workspaceId: string | null) {
  return useMutation({
    mutationFn: () => api.post<CheckoutUrlResponse>(workspacePath(workspaceId!, '/billing/topup')),
  });
}

export function useSmsTopupCheckout(workspaceId: string | null) {
  return useMutation({
    mutationFn: () => api.post<CheckoutUrlResponse>(workspacePath(workspaceId!, '/billing/sms-topup')),
  });
}
